import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Plus, MagnifyingGlass, PencilSimple, Trash, DownloadSimple, UploadSimple, X, UserMinus, ArrowCounterClockwise, Warning, ArrowsLeftRight } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import TransferRequestsPanel from '../components/TransferRequestsPanel';
import { usePermissions } from '../hooks/usePermissions';

const statusLabels = {
  activo: 'Activo',
  'con licencia': 'Con Licencia',
  desvinculado: 'Desvinculado',
  vacaciones: 'Vacaciones',
  suspendido: 'Suspendido',
};

const statusColors = {
  activo: 'badge-activo',
  'con licencia': 'badge-licencia',
  desvinculado: 'badge-desvinculado',
  vacaciones: 'badge-vacaciones',
  suspendido: 'badge-suspendido',
};

const WorkersPage = () => {
  const { axiosInstance, isAdmin, user } = useAuth();
  const { canManage: canManageModule } = usePermissions();
  const isReadOnly = !canManageModule('trabajadores');
  const canSeeValorDia = isAdmin || user?.role === 'encargado_remuneraciones';
  const [workers, setWorkers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [cargoFilter, setCargoFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    first_name: '',
    last_name: '',
    branch_id: '',
    status: 'activo',
    cargo: 'Sin cargo',
    hire_date: new Date().toISOString().split('T')[0],
    valor_dia: 0,
  });
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteHistory, setDeleteHistory] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [showDeleteBlockedModal, setShowDeleteBlockedModal] = useState(false);
  const [desvincularTarget, setDesvincularTarget] = useState(null);
  const [showDesvincularModal, setShowDesvincularModal] = useState(false);
  const [nextCode, setNextCode] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferWorker, setTransferWorker] = useState(null);
  const [transferBranch, setTransferBranch] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [allBranches, setAllBranches] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (branchFilter) params.branch_id = branchFilter;
      if (cargoFilter) params.cargo = cargoFilter;

      const [workersRes, branchesRes, cargosRes] = await Promise.all([
        axiosInstance.get('/api/workers', { params }),
        axiosInstance.get('/api/branches'),
        axiosInstance.get('/api/cargos'),
      ]);
      setWorkers(workersRes.data);
      setBranches(branchesRes.data);
      setCargos(cargosRes.data.filter(c => c.is_active));
      // Load all active branches for transfer dropdown
      try {
        const allRes = await axiosInstance.get('/api/branches/all-active');
        setAllBranches(allRes.data);
      } catch { setAllBranches(branchesRes.data.filter(b => b.is_active !== false)); }
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, search, statusFilter, branchFilter, cargoFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenModal = async (worker = null) => {
    if (worker) {
      setEditingWorker(worker);
      setNextCode('');
      setFormData({
        code: worker.code,
        first_name: worker.first_name,
        second_name: worker.second_name || '',
        last_name: worker.last_name,
        second_last_name: worker.second_last_name || '',
        branch_id: worker.branch_id,
        status: worker.status,
        cargo: worker.cargo || 'Sin cargo',
        contrato: worker.contrato || 'sin contrato',
        hire_date: worker.hire_date || '',
        valor_dia: worker.valor_dia || 0,
      });
    } else {
      setEditingWorker(null);
      const defaultBranch = branches[0]?.id || '';
      setFormData({
        code: '',
        first_name: '',
        second_name: '',
        last_name: '',
        second_last_name: '',
        branch_id: defaultBranch,
        status: 'activo',
        cargo: 'Sin cargo',
        contrato: 'sin contrato',
        hire_date: new Date().toISOString().split('T')[0],
        valor_dia: 0,
      });
      // Fetch next code preview
      if (defaultBranch) {
        try {
          const res = await axiosInstance.get(`/api/branches/${defaultBranch}/next-code`);
          setNextCode(res.data.next_code || '');
        } catch { setNextCode(''); }
      }
    }
    setError('');
    setShowModal(true);
  };

  const handleBranchChange = async (branchId) => {
    setFormData({ ...formData, branch_id: branchId });
    if (!editingWorker && branchId) {
      try {
        const res = await axiosInstance.get(`/api/branches/${branchId}/next-code`);
        setNextCode(res.data.next_code || '');
      } catch { setNextCode(''); }
    }
  };

  const checkDuplicates = useCallback(async (firstName, lastName) => {
    if (!firstName?.trim() || !lastName?.trim() || editingWorker) {
      setDuplicates([]);
      return;
    }
    try {
      const res = await axiosInstance.get('/api/workers/check-duplicate', {
        params: { first_name: firstName.trim(), last_name: lastName.trim() }
      });
      setDuplicates(res.data.duplicates || []);
    } catch { setDuplicates([]); }
  }, [axiosInstance, editingWorker]);

  const handleNameBlur = () => {
    checkDuplicates(formData.first_name, formData.last_name);
  };

  const handleOpenTransfer = (worker) => {
    setTransferWorker(worker);
    setTransferBranch('');
    setShowTransferModal(true);
  };

  const handleTransfer = async () => {
    if (!transferWorker || !transferBranch) return;
    setTransferring(true);
    try {
      const res = await axiosInstance.post('/api/workers/transfer-request', {
        worker_id: transferWorker.id,
        to_branch_id: transferBranch,
      });
      if (res.data.direct) {
        toast.success(`Trasladado a ${res.data.to_branch}`);
      } else {
        toast.success('Solicitud de traspaso enviada al jefe del local destino');
      }
      setShowTransferModal(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al traspasar');
    } finally { setTransferring(false); }
  };

  const handleTransferDuplicate = (dup) => {
    setShowModal(false);
    setDuplicates([]);
    setTransferWorker({ id: dup.id, first_name: dup.full_name, code: dup.code, branch_id: dup.branch_id });
    setTransferBranch(formData.branch_id || '');
    setShowTransferModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (editingWorker) {
        await axiosInstance.put(`/api/workers/${editingWorker.id}`, formData);
      } else {
        await axiosInstance.post('/api/workers', formData);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (worker) => {
    // First check if worker has history
    try {
      const res = await axiosInstance.get(`/api/workers/${worker.id}/history-check`);
      const { has_history, advances_count, daily_records_count } = res.data;

      if (has_history) {
        setDeleteTarget(worker);
        setDeleteHistory({ advances_count, daily_records_count });
        setShowDeleteBlockedModal(true);
        return;
      }

      // No history - show delete confirmation
      setDeleteTarget(worker);
      setDeleteHistory(null);
      setShowDeleteConfirmModal(true);
    } catch (err) {
      toast.error('Error al verificar historial');
    }
  };

  const confirmPhysicalDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await axiosInstance.delete(`/api/workers/${deleteTarget.id}`);
      toast.success('Trabajador eliminado del sistema');
      setShowDeleteConfirmModal(false);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setSaving(false);
    }
  };

  const handleDesvincular = async (worker) => {
    setDesvincularTarget(worker);
    setShowDesvincularModal(true);
  };

  const confirmDesvincular = async () => {
    if (!desvincularTarget) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/api/workers/${desvincularTarget.id}/desvincular`);
      toast.success('Trabajador desvinculado. Su historial se conserva.');
      setShowDesvincularModal(false);
      setDesvincularTarget(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al desvincular');
    } finally {
      setSaving(false);
    }
  };

  const handleReactivar = async (worker) => {
    try {
      await axiosInstance.put(`/api/workers/${worker.id}/reactivar`);
      toast.success('Trabajador reactivado');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reactivar');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get('/api/workers/template/download', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_trabajadores.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error al descargar plantilla');
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImportResult(null);
    setSaving(true);

    try {
      const formDataFile = new FormData();
      formDataFile.append('file', importFile);
      const response = await axiosInstance.post('/api/workers/import', formDataFile, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
      loadData();
    } catch (error) {
      setImportResult({ errors: [{ error: error.response?.data?.detail || 'Error al importar' }] });
    } finally {
      setSaving(false);
    }
  };

  const getBranchName = (branchId) => {
    const branch = branches.find((b) => b.id === branchId);
    return branch?.name || 'Sin sucursal';
  };

  return (
    <div className="space-y-6" data-testid="workers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="workers-title">
            Trabajadores
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Gestión de trabajadores de la empresa
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <button
                onClick={handleDownloadTemplate}
                className="btn-secondary flex items-center gap-2"
                data-testid="download-template-btn"
              >
                <DownloadSimple size={18} />
                Plantilla
              </button>
              <button
                onClick={() => {
                  setShowImportModal(true);
                  setImportFile(null);
                  setImportResult(null);
                }}
                className="btn-secondary flex items-center gap-2"
                data-testid="import-workers-btn"
              >
                <UploadSimple size={18} />
                Importar
              </button>
            </>
          )}
          {!isReadOnly && (
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
            data-testid="add-worker-btn"
          >
            <Plus size={18} />
            Nuevo Trabajador
          </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Buscar por nombre o código..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-input pl-10"
            data-testid="search-workers-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="form-select w-40"
          data-testid="status-filter"
        >
          <option value="">Todos los estados</option>
          {Object.entries(statusLabels).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {(isAdmin || isReadOnly) && (
          <select
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className="form-select w-48"
            data-testid="branch-filter"
          >
            <option value="">Todas las sucursales</option>
            {(isReadOnly ? allBranches.filter(b => (user?.allowed_branches || []).includes(b.id)) : branches).map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        )}
        <select
          value={cargoFilter}
          onChange={(e) => setCargoFilter(e.target.value)}
          className="form-select w-40"
          data-testid="cargo-filter"
        >
          <option value="">Todos los cargos</option>
          {cargos.map((c) => (
            <option key={c.id} value={c.name}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Transfer Requests Panel */}
      <TransferRequestsPanel onTransferComplete={loadData} />

      {/* Workers Table */}
      <div className="table-container">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner"></div>
          </div>
        ) : workers.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            No se encontraron trabajadores
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="workers-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Código
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Nombre
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Sucursal
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Estado
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Cargo
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Contrato
                  </th>
                  {canSeeValorDia && (
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Valor Día
                  </th>
                  )}
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {workers.map((worker) => (
                  <tr
                    key={worker.id}
                    className={`data-row border-b border-zinc-100 ${
                      worker.status === 'desvinculado' ? 'bg-zinc-50 opacity-60' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm font-mono text-zinc-600">
                      {worker.code}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-zinc-900">
                      {worker.first_name} {worker.second_name || ''} {worker.last_name} {worker.second_last_name || ''}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600">
                      {getBranchName(worker.branch_id)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={statusColors[worker.status] || 'badge-inactivo'}>
                        {statusLabels[worker.status] || worker.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600">
                      {worker.cargo || 'Sin cargo'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 ${worker.contrato === 'con contrato' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-zinc-100 text-zinc-500 border border-zinc-200'}`}>
                        {worker.contrato === 'con contrato' ? 'Con contrato' : 'Sin contrato'}
                      </span>
                    </td>
                    {canSeeValorDia && (
                    <td className="px-4 py-3 text-sm text-right font-mono text-zinc-700">
                      {worker.valor_dia ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(worker.valor_dia) : '-'}
                    </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {!isReadOnly && (
                        <button
                          onClick={() => handleOpenModal(worker)}
                          className="p-1.5 text-zinc-400 hover:text-zinc-900 transition-colors"
                          title="Editar"
                          data-testid={`edit-worker-${worker.id}`}
                        >
                          <PencilSimple size={17} />
                        </button>
                        )}
                        {!isReadOnly && worker.status !== 'desvinculado' && (
                          <button
                            onClick={() => handleOpenTransfer(worker)}
                            className="p-1.5 text-zinc-400 hover:text-blue-600 transition-colors"
                            title="Traspasar a otra sucursal"
                            data-testid={`transfer-worker-${worker.id}`}
                          >
                            <ArrowsLeftRight size={17} />
                          </button>
                        )}
                        {isAdmin && worker.status !== 'desvinculado' && (
                          <button
                            onClick={() => handleDesvincular(worker)}
                            className="p-1.5 text-zinc-400 hover:text-amber-600 transition-colors"
                            title="Desvincular (mantener historial)"
                            data-testid={`desvincular-worker-${worker.id}`}
                          >
                            <UserMinus size={17} />
                          </button>
                        )}
                        {isAdmin && worker.status === 'desvinculado' && (
                          <button
                            onClick={() => handleReactivar(worker)}
                            className="p-1.5 text-zinc-400 hover:text-emerald-600 transition-colors"
                            title="Reactivar trabajador"
                            data-testid={`reactivar-worker-${worker.id}`}
                          >
                            <ArrowCounterClockwise size={17} />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => handleDelete(worker)}
                            className="p-1.5 text-zinc-400 hover:text-red-600 transition-colors"
                            title="Eliminar del sistema"
                            data-testid={`delete-worker-${worker.id}`}
                          >
                            <Trash size={17} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Worker Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingWorker ? 'Editar Trabajador' : 'Nuevo Trabajador'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="alert-error text-sm">{error}</div>}
            {editingWorker ? (
              <div>
                <label className="form-label">Código</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  className="form-input font-mono"
                  required
                  data-testid="worker-code-input"
                />
              </div>
            ) : (
              <div>
                <label className="form-label">Código</label>
                <div className="flex items-center gap-2">
                  <div className="form-input bg-zinc-50 text-zinc-500 font-mono flex-1 flex items-center" data-testid="worker-code-preview">
                    {nextCode ? (
                      <span>{nextCode} <span className="text-xs text-zinc-400">(auto)</span></span>
                    ) : (
                      <span className="text-zinc-400 text-sm">Se generará automáticamente</span>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Primer Nombre</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  onBlur={handleNameBlur}
                  className="form-input"
                  required
                  data-testid="worker-firstname-input"
                />
              </div>
              <div>
                <label className="form-label">Segundo Nombre</label>
                <input
                  type="text"
                  value={formData.second_name || ''}
                  onChange={(e) => setFormData({ ...formData, second_name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="worker-secondname-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Primer Apellido</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  onBlur={handleNameBlur}
                  className="form-input"
                  required
                  data-testid="worker-lastname-input"
                />
              </div>
              <div>
                <label className="form-label">Segundo Apellido</label>
                <input
                  type="text"
                  value={formData.second_last_name || ''}
                  onChange={(e) => setFormData({ ...formData, second_last_name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="worker-secondlastname-input"
                />
              </div>
            </div>
            {/* Duplicate Alert */}
            {!editingWorker && duplicates.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 p-3 space-y-2" data-testid="duplicate-alert">
                <div className="flex items-center gap-2">
                  <Warning size={16} className="text-amber-600 shrink-0" />
                  <span className="text-xs font-semibold text-amber-800">Ya existe un trabajador con el mismo nombre</span>
                </div>
                {duplicates.map(dup => (
                  <div key={dup.id} className="bg-white border border-amber-100 p-2 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-zinc-900 truncate">{dup.full_name}</div>
                      <div className="text-[10px] text-zinc-500">
                        <span className="font-mono">{dup.code}</span> &middot; {dup.branch_name} &middot; {dup.status} &middot; {dup.cargo}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleTransferDuplicate(dup)}
                      className="px-2 py-1 text-[10px] font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors shrink-0 flex items-center gap-1"
                      data-testid={`transfer-dup-${dup.id}`}
                    >
                      <ArrowsLeftRight size={12} /> Traspasar
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <label className="form-label">Sucursal</label>
              <select
                value={formData.branch_id}
                onChange={(e) => handleBranchChange(e.target.value)}
                className="form-select"
                required
                data-testid="worker-branch-select"
              >
                <option value="">Seleccionar sucursal</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Estado</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="form-select"
                data-testid="worker-status-select"
              >
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Cargo</label>
              <select
                value={formData.cargo}
                onChange={(e) => {
                  const selectedCargo = cargos.find(c => c.name === e.target.value);
                  const newData = { ...formData, cargo: e.target.value };
                  if (!editingWorker) {
                    newData.valor_dia = selectedCargo?.anticipo_diario || 0;
                  }
                  setFormData(newData);
                }}
                className="form-select"
                data-testid="worker-cargo-select"
              >
                {cargos.map((cargo) => (
                  <option key={cargo.id} value={cargo.name}>
                    {cargo.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Contrato</label>
              <select
                value={formData.contrato || 'sin contrato'}
                onChange={(e) => setFormData({ ...formData, contrato: e.target.value })}
                className="form-select"
                data-testid="worker-contrato-select"
              >
                <option value="con contrato">Con contrato</option>
                <option value="sin contrato">Sin contrato</option>
              </select>
            </div>
            <div>
              <label className="form-label">Fecha de Ingreso</label>
              <input
                type="date"
                value={formData.hire_date}
                onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                className="form-input"
                data-testid="worker-hire-date"
              />
            </div>
            {isAdmin && (
            <div>
              <label className="form-label">Valor Día (CLP)</label>
              {!editingWorker ? (
                <div className="form-input bg-zinc-50 font-mono text-zinc-500" data-testid="worker-valor-dia-input">
                  {formData.valor_dia ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(formData.valor_dia) : '$0'}
                  <span className="text-[10px] text-zinc-400 ml-2">(asignado por cargo)</span>
                </div>
              ) : (
                <input
                  type="number"
                  value={formData.valor_dia}
                  onChange={(e) => setFormData({ ...formData, valor_dia: parseFloat(e.target.value) || 0 })}
                  className="form-input font-mono"
                  min="0"
                  step="1000"
                  placeholder="0"
                  data-testid="worker-valor-dia-input"
                />
              )}
            </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="rounded-none bg-zinc-900">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Importar Trabajadores</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              Suba un archivo Excel con las columnas: codigo, sucursal, nombres, apellidos, situacion_actual, cargo, fecha_ingreso{isAdmin ? ', valor_dia' : ''}
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files[0])}
              className="form-input"
              data-testid="import-file-input"
            />
            {importResult && (
              <div className={importResult.imported > 0 || importResult.updated > 0 ? 'alert-success' : 'alert-warning'}>
                <p className="font-medium">
                  {importResult.imported > 0 && `${importResult.imported} nuevos importados. `}
                  {importResult.updated > 0 && `${importResult.updated} existentes actualizados. `}
                  {importResult.imported === 0 && importResult.updated === 0 && 'No se procesaron trabajadores.'}
                </p>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 text-sm list-disc list-inside">
                    {importResult.errors.slice(0, 5).map((err) => (
                      <li key={`err-${err.row}-${err.error?.slice(0,20)}`}>
                        {err.row ? `Fila ${err.row}: ` : ''}{err.error}
                      </li>
                    ))}
                    {importResult.errors.length > 5 && (
                      <li>...y {importResult.errors.length - 5} errores más</li>
                    )}
                  </ul>
                )}
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowImportModal(false)}
                className="rounded-none"
              >
                Cerrar
              </Button>
              <Button
                onClick={handleImport}
                disabled={!importFile || saving}
                className="rounded-none bg-zinc-900"
                data-testid="import-submit-btn"
              >
                {saving ? 'Importando...' : 'Importar'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal - No history */}
      <Dialog open={showDeleteConfirmModal} onOpenChange={setShowDeleteConfirmModal}>
        <DialogContent className="sm:max-w-md rounded-none border-red-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-700">
              <Trash size={20} />
              Eliminar Trabajador del Sistema
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200">
              <p className="text-sm text-red-800 font-medium">
                Esta acción es irreversible.
              </p>
              <p className="text-sm text-red-700 mt-1">
                Se eliminará permanentemente a <strong>{deleteTarget?.first_name} {deleteTarget?.last_name}</strong> ({deleteTarget?.code}) del sistema.
              </p>
            </div>
            <p className="text-sm text-zinc-600">
              Este trabajador no tiene historial asociado, por lo que puede eliminarse de forma segura.
            </p>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDeleteConfirmModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmPhysicalDelete}
                disabled={saving}
                className="rounded-none bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                {saving ? 'Eliminando...' : 'Eliminar Permanentemente'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Blocked Modal - Has history */}
      <Dialog open={showDeleteBlockedModal} onOpenChange={setShowDeleteBlockedModal}>
        <DialogContent className="sm:max-w-md rounded-none border-amber-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-amber-700">
              <Warning size={20} />
              No se puede eliminar
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>{deleteTarget?.first_name} {deleteTarget?.last_name}</strong> ({deleteTarget?.code}) tiene historial asociado:
              </p>
              <ul className="mt-2 text-sm text-amber-700 list-disc list-inside">
                {deleteHistory?.advances_count > 0 && (
                  <li>{deleteHistory.advances_count} anticipo(s) registrado(s)</li>
                )}
                {deleteHistory?.daily_records_count > 0 && (
                  <li>{deleteHistory.daily_records_count} registro(s) diario(s)</li>
                )}
              </ul>
            </div>
            <p className="text-sm text-zinc-600">
              Para conservar la trazabilidad histórica, este trabajador no puede eliminarse del sistema. 
              En su lugar, use <strong>&ldquo;Desvincular&rdquo;</strong> para marcarlo como desvinculado manteniendo todo su historial.
            </p>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDeleteBlockedModal(false)}
                className="rounded-none"
              >
                Cerrar
              </Button>
              <Button
                onClick={() => {
                  setShowDeleteBlockedModal(false);
                  handleDesvincular(deleteTarget);
                }}
                className="rounded-none bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="desvincular-instead-btn"
              >
                <UserMinus size={16} className="mr-2" />
                Desvincular en su lugar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Desvincular Confirmation Modal */}
      <Dialog open={showDesvincularModal} onOpenChange={setShowDesvincularModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserMinus size={20} className="text-amber-600" />
              Desvincular Trabajador
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              Se cambiará el estado de <strong>{desvincularTarget?.first_name} {desvincularTarget?.last_name}</strong> ({desvincularTarget?.code}) a <strong>Desvinculado</strong>.
            </p>
            <div className="p-3 bg-zinc-50 border border-zinc-200 space-y-1">
              <p className="text-sm text-zinc-700 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                Su historial se conservará intacto
              </p>
              <p className="text-sm text-zinc-700 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                Seguirá visible en listados y reportes
              </p>
              <p className="text-sm text-zinc-700 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                No estará disponible para nuevos registros operativos
              </p>
              <p className="text-sm text-zinc-700 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                Puede reactivarse en el futuro si es necesario
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDesvincularModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmDesvincular}
                disabled={saving}
                className="rounded-none bg-zinc-900"
                data-testid="confirm-desvincular-btn"
              >
                {saving ? 'Procesando...' : 'Confirmar Desvinculación'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-sm rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">Traspasar Trabajador</DialogTitle>
          </DialogHeader>
          {transferWorker && (
            <div className="space-y-4">
              <div className="bg-zinc-50 border border-zinc-200 p-3">
                <div className="text-sm font-medium text-zinc-900">
                  {transferWorker.first_name} {transferWorker.second_name || ''} {transferWorker.last_name || ''} {transferWorker.second_last_name || ''}
                </div>
                <div className="text-xs text-zinc-500 font-mono mt-0.5">{transferWorker.code}</div>
              </div>
              <div>
                <label className="form-label">Sucursal Destino</label>
                <select
                  value={transferBranch}
                  onChange={(e) => setTransferBranch(e.target.value)}
                  className="form-select"
                  data-testid="transfer-branch-select"
                >
                  <option value="">Seleccionar sucursal</option>
                  {allBranches.filter(b => b.id !== transferWorker.branch_id).map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              {isAdmin && (
                <p className="text-[10px] text-zinc-400">Como admin, el traspaso se ejecuta inmediatamente.</p>
              )}
              {!isAdmin && (
                <p className="text-[10px] text-zinc-400">Se enviara una solicitud al jefe del local destino para su aprobacion.</p>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowTransferModal(false)} className="rounded-none">
                  Cancelar
                </Button>
                <Button
                  type="button"
                  onClick={handleTransfer}
                  disabled={!transferBranch || transferring}
                  className="rounded-none bg-blue-600 hover:bg-blue-700"
                  data-testid="confirm-transfer-btn"
                >
                  {transferring ? 'Procesando...' : isAdmin ? 'Traspasar' : 'Enviar Solicitud'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkersPage;
